import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { api } from '@/services/api';
import type { FamilyApplication } from '@/types/applications';
import type { GrantCurrency, GrantRequest } from '@/types/api';
import { formatMoney, monthLabel } from '@/lib/money';
import {
  APPLICATION_STATUS_DISPLAY,
  DOCUMENT_KIND_LABEL_KEY,
  canFamilyWithdraw,
  familyStatusLabelKey,
  familyVisibleStage,
  familyVisibleStatus,
  type FamilyVisibleStage,
} from '@/lib/applications-display';
import { formatPreset } from '@/i18n/intl';
import { TRF_TIMEZONE } from '@/lib/trfTime';
import { useMsg } from '@/i18n/msg';
import { useFlaggedFieldLabelState } from '@/lib/flagged-fields';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MonoLabel, SectionTitle, StatusChip } from '@/components/editorial';
import { useCopy } from '@/hooks/useCopy';
import type { ProseSlotKey } from '@/lib/copyResolve';
import { documentKindLabel } from '@/lib/documents';
import { renewalMarkOf } from '@/lib/renewal-mark';
import { useToast } from '@/components/ui/use-toast';
import { isRefusedTransition } from '@/lib/refusedTransition';
import { serverCode } from '@/lib/serverMessage';

/** The family route's refusal from committee approval on (backend models/applicationWithdrawRules.js). */
const WITHDRAW_NEEDS_STAFF = 'WITHDRAW_NEEDS_STAFF';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * #1039 / AP-26 (PR-C): the wording on this screen resolves through `copy()`,
 * so TRF can change it without a developer. The seeds in `lib/copySlots.ts` are
 * these exact sentences, so with the store empty or the API unreachable this
 * page renders byte-identically to what it rendered before.
 *
 * Two things on this screen are deliberately NOT registrable, and each for its
 * own reason (both recorded in the registry's exclusion note):
 * - `APPLICATION_STATUS_DISPLAY` is shared with ApplicationDecisionDialog and
 *   ApplicationsQueuePage, so it is a staff/family map — D-CS-1's ownership
 *   rule. A comms edit meant for this screen would silently change two internal
 *   ones, and the queue page lowercases the label mid-sentence.
 * - The `flagged_fields` list is data, not copy: each flagged field reads in
 *   the form's own words through `useFlaggedFieldLabels` (#2520), with a
 *   humanized path only as the fallback. A registry cannot reach a value that
 *   is data.
 */

type CopyFn = ReturnType<typeof useCopy>['copy'];

// ── Timeline ─────────────────────────────────────────────────────────────────

/**
 * The three steps light off the family-visible STAGE, never off the raw status
 * (#1271).
 *
 * They used to list statuses, and the list said `approved` / `denied` — two
 * strings epic #1037 stopped writing. After
 * `backend/scripts/migrate-application-statuses.mjs` ran, no real application
 * matched the decision step any more, so every decided family saw an unlit
 * timeline. A per-status list here is a list that goes stale silently; the
 * stage projection in `lib/applications-display.ts` is exhaustive over the
 * status union, so the compiler asks about the next new state instead.
 */
type TimelineNode = { id: string; label: ProseSlotKey; stages: FamilyVisibleStage[] };
const DECIDED: FamilyVisibleStage[] = ['approved', 'declined', 'waitlisted'];
const TIMELINE_NODES: TimelineNode[] = [
  { id: 'submitted', label: 'apply.status.timeline_submitted', stages: ['submitted', 'review', ...DECIDED] },
  { id: 'review', label: 'apply.status.timeline_review', stages: ['review', ...DECIDED] },
  { id: 'decision', label: 'apply.status.timeline_decision', stages: [...DECIDED] },
];

/**
 * WHICH PARAGRAPH A FAMILY READS under the timeline (#1135, B-444, ruled
 * 2026-08-23).
 *
 * This used to be one unconditional `copy('apply.status.body')`, so a family
 * whose application had been APPROVED read “A TRF case manager will review your
 * application … we’ll be in touch when a decision is made” immediately above
 * the note carrying the decision. The PR-C walk found it; a unit test could
 * not have, because every assertion the screen had was written against the one
 * status it was rendered in.
 *
 * A MAP, NOT A CHAIN OF TERNARIES, and it is deliberately PARTIAL: a stage
 * absent from it falls back to the undecided paragraph, which is what today
 * says and stays true. `none` — `withdrawn` and `draft` — is the one such
 * stage; the ruling named three outcomes, and inventing a fourth slot here
 * would be writing a ruling rather than following one. Its gap is pinned by a
 * test that says so.
 *
 * #1271 rekeyed it from status to STAGE. Keyed on statuses it named the two
 * strings the #1037 migration retired, so a decided family fell through to the
 * undecided paragraph — the very defect this map was added to fix, arriving a
 * second time through a rename. The three slots are unchanged: `staff_declined`
 * reads what `denied` read, and the offer chain reads what `approved` read.
 */
const OUTCOME_BODY = {
  approved: 'apply.status.body_approved',
  declined: 'apply.status.body_denied',
  waitlisted: 'apply.status.body_waitlisted',
} as const satisfies Partial<Record<FamilyVisibleStage, ProseSlotKey>>;

const bodySlotFor = (stage: FamilyVisibleStage): ProseSlotKey =>
  OUTCOME_BODY[stage as keyof typeof OUTCOME_BODY] ?? 'apply.status.body';

/**
 * #2400: the headline follows the same stage. It was one unconditional slot,
 * so a declined family read “It’s in our hands now.” above the decline. Same
 * shape as OUTCOME_BODY and the same partial map: a stage absent from it — still
 * deciding, or a decision the family has not been told — keeps the waiting
 * headline. `withdrawn` is handled at the call site from the catalog, as its
 * paragraph is.
 */
const OUTCOME_HEADLINE = {
  approved: 'apply.status.headline_approved',
  declined: 'apply.status.headline_denied',
  waitlisted: 'apply.status.headline_waitlisted',
} as const satisfies Partial<Record<FamilyVisibleStage, ProseSlotKey>>;

const headlineSlotFor = (stage: FamilyVisibleStage): ProseSlotKey =>
  OUTCOME_HEADLINE[stage as keyof typeof OUTCOME_HEADLINE] ?? 'apply.status.headline';

/**
 * #2520: a family TRF has asked for something reads that, not the waiting
 * pair. Its stage stays `review` (the timeline is still right: TRF is
 * reviewing), so this keys on the STATUS, ahead of the stage maps above. The
 * flagged-items panel under the timeline then names what was asked.
 */
const NEEDS_INFO_SLOTS = {
  headline: 'apply.status.headline_needs_info',
  body: 'apply.status.body_needs_info',
} as const satisfies Record<string, ProseSlotKey>;

/**
 * E2E 2026-10-13: from the welcome email to admission, each step says what is
 * true at that step. All three are stage `approved`, so the stage maps above
 * gave them one paragraph — “Someone from TRF will be in touch about what
 * happens next” — and an ADMITTED family was never told it was in, or where
 * its grant was. Keyed on the STATUS ahead of the stage maps, like the
 * needs-info pair.
 *
 * A family renewing through /apply (`renewalMarkOf`, the rule its welcome email
 * reads, #2869) reads renewal words. `offer_signed` has one line for both: it
 * only says the acceptance is in. A headline absent here keeps the stage's.
 * No line promises a letter or a signature (#2735): there is none until
 * DocuSign (#1043); TRF records the acceptance by hand.
 */
type AdmissionSlots = { headline?: ProseSlotKey; body: ProseSlotKey; grantsCta?: true };
const ADMISSION_SLOTS = {
  offer_sent: {
    new: { body: 'apply.status.body_offer_sent' },
    renewal: { body: 'apply.status.body_offer_sent_renewal' },
  },
  offer_signed: {
    new: { body: 'apply.status.body_acceptance_recorded' },
    renewal: { body: 'apply.status.body_acceptance_recorded' },
  },
  admitted: {
    new: { headline: 'apply.status.headline_admitted', body: 'apply.status.body_admitted', grantsCta: true },
    renewal: {
      headline: 'apply.status.headline_admitted_renewal',
      body: 'apply.status.body_admitted_renewal',
      grantsCta: true,
    },
  },
} as const satisfies Partial<Record<FamilyApplication['status'], Record<'new' | 'renewal', AdmissionSlots>>>;

const admissionSlotsFor = (app: FamilyApplication): AdmissionSlots | null => {
  const byKind = ADMISSION_SLOTS[app.status as keyof typeof ADMISSION_SLOTS];
  if (!byKind) return null;
  return renewalMarkOf(app) ? byKind.renewal : byKind.new;
};

/**
 * #2400 follow-up: the eyebrow read “Application received” above every
 * headline, a decline and a withdrawal included. While TRF is deciding it stays
 * true. Once the headline names an outcome, the eyebrow is one neutral line:
 * the chip under the headline already names the outcome, so a per-outcome
 * eyebrow would only say it twice.
 */
const eyebrowSlotFor = (stage: FamilyVisibleStage, withdrawn: boolean): ProseSlotKey =>
  withdrawn || stage in OUTCOME_HEADLINE ? 'apply.status.eyebrow_decided' : 'apply.status.eyebrow';

function TimelineView({ stage, copy }: { stage: FamilyVisibleStage; copy: CopyFn }) {
  return (
    <div className="flex items-start gap-0 w-full max-w-sm">
      {TIMELINE_NODES.map((node, i) => {
        const isActive = TIMELINE_NODES[i].stages.includes(stage);
        const isLast = i === TIMELINE_NODES.length - 1;
        return (
          // `data-*`, not a class assertion: the test for #1271 asks which steps
          // are LIT, and a test that reads Tailwind class names would pass on a
          // restyle that lit nothing.
          <div
            key={node.id}
            data-timeline-node={node.id}
            data-active={isActive}
            className="flex-1 flex flex-col items-center"
          >
            <div className="flex items-center w-full">
              {/* Dot */}
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors ${
                  isActive
                    ? 'border-stevens-indigo-solid bg-stevens-indigo-solid'
                    : 'border-border bg-background'
                }`}
              >
                {isActive ? (
                  <CheckCircle2 size={14} className="text-stevens-indigo-solid-fg" aria-hidden />
                ) : (
                  <Clock size={12} className="text-stevens-ink-3" aria-hidden />
                )}
              </div>
              {/* Connector */}
              {!isLast && (
                <div
                  className={`h-0.5 flex-1 transition-colors ${
                    TIMELINE_NODES[i + 1]?.stages.includes(stage)
                      ? 'bg-stevens-indigo'
                      : 'bg-border'
                  }`}
                />
              )}
            </div>
            {/* Label */}
            <MonoLabel
 className={`mt-2 text-center font-normal leading-tight ${
                isActive ? 'text-stevens-indigo' : 'text-stevens-ink-3'
              }`}
            >
              {copy(node.label)}
            </MonoLabel>
          </div>
        );
      })}
    </div>
  );
}

// ── What happens next, once admitted (#2977) ───────────────────────────────

/**
 * #2977: the first payment of a new family's Signature grant, as their own
 * `GET /grants` already carries it (backend helpers/familyFacingProjection.js
 * `projectAwardForFamily`: `monthly_cents`, `start_month`, `currency`).
 *
 * `undefined` — not known here (the copy editor's preview, a failed read, no
 * live Signature grant): the line is left out, because nothing true can be said.
 * `null` — the grant exists but staff have not dated its schedule yet (the
 * desk's Decide sets `start_month`), so the family is told where it will show.
 */
export type SignatureStart = { monthly_cents: number; currency: GrantCurrency; start_month: string };

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The family's newest live Signature grant's first payment, read off `GET /grants`. */
function signatureStartOf(grants: GrantRequest[] | null | undefined): SignatureStart | null | undefined {
  const live = (grants ?? []).filter(
    (g) => g.type === 'signature' && g.status !== 'denied' && g.status !== 'withdrawn',
  );
  if (live.length === 0) return undefined;
  const newest = live.reduce((a, b) => ((b.created_at ?? '') > (a.created_at ?? '') ? b : a));
  const schedule = newest.award?.schedule;
  if (!schedule || !(schedule.monthly_cents > 0) || !schedule.start_month || !MONTH_KEY.test(schedule.start_month)) {
    return null;
  }
  return {
    monthly_cents: schedule.monthly_cents,
    currency: newest.award?.currency ?? 'USD',
    start_month: schedule.start_month,
  };
}

/**
 * #2977 (Azalea gap report AZ-6, AZ-10, X-1): what an admitted family can be
 * told comes next, from facts the platform holds. Two lines:
 * - the grant's monthly amount and first payment month, or that the month
 *   shows here once TRF sets the grant up;
 * - Travel and Family Fun are grants the family may be able to apply for
 *   later. The award letter names them as "if funding is available".
 * #3092: the cycle's award terms add two facts. The onboarding meeting, on
 * TRF's clock (Pacific, the zone named), once staff set its date. And the
 * first payment month the cycle plans, until the family's own grant has a
 * schedule — the grant's month is the truth once it exists.
 * Left out on purpose: any letter (no line on this screen promises one, #2735).
 */
type CycleAwardTermsForFamily = NonNullable<FamilyApplication['cycle_award_terms']>;

const MEETING_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
};

function AdmittedNextSteps({
  signatureStart, cycleTerms,
}: { signatureStart?: SignatureStart | null; cycleTerms?: CycleAwardTermsForFamily | null }) {
  const { t } = useTranslation('applications');
  const month = signatureStart ? monthLabel(signatureStart.start_month) : null;
  const plannedMonth = !signatureStart && cycleTerms?.first_payment_month && MONTH_KEY.test(cycleTerms.first_payment_month)
    ? monthLabel(cycleTerms.first_payment_month)
    : null;
  const meetingAt = cycleTerms?.onboarding_meeting_at;
  const meeting = meetingAt && !Number.isNaN(new Date(meetingAt).getTime())
    ? formatPreset(meetingAt, MEETING_FORMAT, { timeZone: TRF_TIMEZONE })
    : null;
  return (
    <section
      aria-labelledby="admitted-next-steps-title"
      className="space-y-3 border-t border-border pt-6"
      data-testid="admitted-next-steps"
    >
      <SectionTitle level="card-title" as="h2" id="admitted-next-steps-title">
        {t('statusPage.nextSteps.title')}
      </SectionTitle>
      {signatureStart && month && (
        <p className="text-body leading-relaxed text-stevens-ink-2" data-testid="next-steps-first-payment">
          {t('statusPage.nextSteps.firstPayment', {
            amount: formatMoney(signatureStart.monthly_cents, signatureStart.currency),
            month,
          })}
        </p>
      )}
      {plannedMonth && (
        <p className="text-body leading-relaxed text-stevens-ink-2" data-testid="next-steps-first-payment">
          {t('statusPage.nextSteps.firstPaymentPlanned', { month: plannedMonth })}
        </p>
      )}
      {signatureStart === null && !plannedMonth && (
        <p className="text-body leading-relaxed text-stevens-ink-2" data-testid="next-steps-first-payment">
          {t('statusPage.nextSteps.firstPaymentPending')}
        </p>
      )}
      {meeting && (
        <p className="text-body leading-relaxed text-stevens-ink-2" data-testid="next-steps-onboarding-meeting">
          {t('statusPage.nextSteps.onboardingMeeting', { meeting })}
        </p>
      )}
      <p className="text-body leading-relaxed text-stevens-ink-2">
        {t('statusPage.nextSteps.moreGrants')}
      </p>
    </section>
  );
}

// ── The screen itself ────────────────────────────────────────────────────────

/**
 * The status screen, from an application and nothing else.
 *
 * Split out of the page for the copy editor's preview (#1039 PR-D, D-CS-12),
 * which must render THE REAL SCREEN from draft wording rather than a facsimile
 * that agrees with it today and drifts from it next month. The page keeps the
 * fetch, the redirect and the loading gate — none of which a preview can have,
 * since `getMine()` is a family's own application and its failure path
 * navigates away.
 *
 * `onContinue` is the one interaction, injected because the preview has
 * nowhere to send anyone.
 */
/** A stored value the reader cannot act on as an identifier: `household_income` → "household income". */
const humanize = (raw: string) => raw.replace(/_/g, ' ');

export function ApplicationStatusView({
  app,
  copy,
  onContinue,
  fieldLabel,
  fieldLabelsPending = false,
  onWithdraw,
  onOpenGrants,
  signatureStart,
}: {
  app: FamilyApplication;
  copy: CopyFn;
  onContinue: () => void;
  /**
   * E2E 2026-10-13: an admitted family's way to its grant. Absent — the copy
   * editor's preview, which has nowhere to send anyone — the button still
   * renders, so its wording can be seen, and does nothing.
   */
  onOpenGrants?: () => void;
  /**
   * #2977: the Signature grant's first payment, for the admitted family's
   * "What happens next" block (see `SignatureStart`). Absent, the payment line
   * is left out.
   */
  signatureStart?: SignatureStart | null;
  /**
   * #2236: withdraw this application. Absent — the copy editor's preview, which
   * has nobody to withdraw — the control is not rendered at all.
   */
  onWithdraw?: () => Promise<void>;
  /**
   * A flagged field's question, in the reader's language, from the published
   * form version (R2's `localizeVersion`). Absent — the copy editor's preview,
   * a definition still loading or failed to load — the stored key is shown
   * humanized, as before.
   */
  fieldLabel?: (flag: string) => string | null;
  /**
   * #2544: the form version that names the flagged fields is still loading.
   * The list waits (a placeholder per item) rather than flash a storage path;
   * a failed load clears this and the humanized key shows, as before.
   */
  fieldLabelsPending?: boolean;
}) {
  const { t, i18n } = useTranslation(['applications', 'common']);
  const resolve = useMsg();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const english = i18n.language === 'en' || i18n.language.startsWith('en-');
  // #1271 / #998: the family reads a stage, and the chip names only a status
  // they have been told about. `familyVisibleStatus` is the identity for all but
  // `staff_recommended` / `committee_approved`, which borrow `under_review`.
  const stage = familyVisibleStage(app.status);
  const admission = admissionSlotsFor(app);
  const headlineSlot: ProseSlotKey = app.status === 'needs_more_info'
    ? NEEDS_INFO_SLOTS.headline
    : admission?.headline ?? headlineSlotFor(stage);
  const bodySlot: ProseSlotKey = app.status === 'needs_more_info'
    ? NEEDS_INFO_SLOTS.body
    : admission?.body ?? bodySlotFor(stage);
  const display = APPLICATION_STATUS_DISPLAY[familyVisibleStatus(app.status)];
  // The chip's WORD comes from the family-visible key map (#2109 R3-B4); its
  // tone stays with the status display map.
  // DECISIONS 2026-10-16: the platform does not communicate a decline or a
  // waitlist, so the chip names neither. Both read "Reviewed".
  const reviewedOnly = stage === 'declined' || stage === 'waitlisted';
  const labelKey = reviewedOnly ? 'applications:status.reviewed' : familyStatusLabelKey(app.status);
  const chipTone = reviewedOnly ? 'neutral' : display.tone;
  const statusLabel = labelKey ? resolve({ key: labelKey }) : display.label;
  // Staff free text (the decision note, a document's reason) is shown as TRF
  // wrote it, with one line saying so for a non-English reader.
  const englishNote = english ? null : (
    <span className="block text-body-sm text-stevens-ink-3" data-testid="shown-in-english">
      {t('statusPage.shownInEnglish')}
    </span>
  );
  return (
    <div className="max-w-2xl mx-auto px-4 py-14 space-y-10">
      {/* Eyebrow */}
      <MonoLabel tone="indigo" className="font-normal">
        <Sparkles size={10} className="inline mr-1.5 -mt-px" aria-hidden />
        {copy(eyebrowSlotFor(stage, app.status === 'withdrawn'))}
      </MonoLabel>

      {/* Hero headline */}
      <h1
        className="text-title font-normal leading-tight text-foreground font-serif"
      >
        {app.status === 'withdrawn' ? t('withdraw.withdrawnHeadline') : copy(headlineSlot)}
      </h1>

      {/* Status badge */}
      <div className="flex items-center gap-3">
        <StatusChip tone={chipTone}>
          {statusLabel}
        </StatusChip>
        {app.submitted_at && (
          <span className="text-body text-stevens-ink-3">
            {/* One copy slot with {{date}} (lane L9); the date goes through
                Intl in the reader's language (R3-B5). */}
            {copy('apply.status.submitted_prefix', {
              date: formatPreset(app.submitted_at, 'longDate'),
            })}
          </span>
        )}
      </div>

      {/* Timeline */}
      <TimelineView stage={stage} copy={copy} />

      {/* Body copy — chosen by where this family actually is (the stage, or
          the status for needs-info and the offer-to-admission steps).
          #2236: a withdrawn application no longer reads the waiting paragraph
          ("we'll be in touch when a decision is made") — once a family can
          withdraw, that sentence would be false. A catalog line, not a fourth
          copy slot: the 2026-08-23 ruling named three outcomes. */}
      <p
        className="text-lede leading-relaxed text-foreground"
      >
        {app.status === 'withdrawn' ? t('withdraw.withdrawnBody') : copy(bodySlot)}
      </p>

      {/* E2E 2026-10-13: admission creates the Signature grant (#2808), so an
          admitted family's next place is the Grants page. */}
      {/* #2977: a newly admitted family reads what comes next. A renewing
          family already knows the program, so the block is for `new` only. */}
      {app.status === 'admitted' && !renewalMarkOf(app) && (
        <AdmittedNextSteps signatureStart={signatureStart} cycleTerms={app.cycle_award_terms} />
      )}

      {admission?.grantsCta && (
        <Button type="button" variant="brand" onClick={onOpenGrants}>
          {copy('apply.status.grants_cta')}
        </Button>
      )}

      {/* Mario, 2026-10-13: a withdrawal is not the end of the road. Staff can
          reopen it, and the family is told how to ask. */}
      {app.status === 'withdrawn' && (
        <p className="text-body leading-relaxed text-stevens-ink-2" data-testid="withdrawn-contact">
          {t('withdraw.reopenContact')}
        </p>
      )}

      {/* Needs more info */}
      {app.status === 'needs_more_info' && (
        <div className="border border-status-warn-border bg-status-warn rounded-xl p-5 space-y-4">
          <MonoLabel
 className="block font-normal text-status-warn-fg"
          >
            {copy('apply.status.needs_info_label')}
          </MonoLabel>
          {/* #2527: the message staff wrote with the request, attributed and
              quoted like a document's reason (D-SE-17) — the follow-up email
              already carries it, and it is often the whole explanation. */}
          {app.more_info_message && (
            <p className="text-body text-status-warn-fg" data-testid="more-info-message">
              {copy('apply.status.documents_attribution')}{' '}
              <span className="italic" lang="en">“{app.more_info_message}”</span>
              {englishNote}
            </p>
          )}
          {app.flagged_fields.length > 0 && (
            <ul className="space-y-1" aria-busy={fieldLabelsPending || undefined}>
              {app.flagged_fields.map((f) => (
                <li
                  key={f}
                  className="text-body text-status-warn-fg"
                >
                  {fieldLabelsPending ? (
                    <Skeleton className="inline-block h-4 w-48 align-middle" data-testid="flagged-field-pending" aria-hidden />
                  ) : (
                    <>• {fieldLabel?.(f) ?? humanize(f)}</>
                  )}
                </li>
              ))}
            </ul>
          )}
          {/* AP-23 (#1006): documents asked for again, each with the reason.
              The reason is the whole difference between this and the
              all-or-nothing reopen — a family told "resubmit the insurance
              card" with nothing else is being asked to guess what was wrong
              with the one they sent. It is required server-side for that
              reason, so it is always here to render. */}
          {(app.flagged_documents ?? []).length > 0 && (
            <ul className="space-y-2" data-testid="flagged-documents">
              {(app.flagged_documents ?? []).map((d) => (
                <li key={d.kind} className="text-body text-status-warn-fg">
                  {/* D-SE-18: the family is never shown a storage key. The kind
                      resolves through the `applications` catalog in the reader's
                      language (#2109 R3). A kind the catalog does not carry falls
                      back to `documentKindLabel`, which humanizes and is pinned to
                      the server's vocabulary by
                      backend/tests/contract/document-kind-label-parity.test.js. */}
                  • {DOCUMENT_KIND_LABEL_KEY[d.kind as keyof typeof DOCUMENT_KIND_LABEL_KEY]
                    ? resolve({ key: DOCUMENT_KIND_LABEL_KEY[d.kind as keyof typeof DOCUMENT_KIND_LABEL_KEY] })
                    : documentKindLabel(d.kind)}
                  {/* D-SE-17: the case manager's words, attributed and quoted, so
                      the family can tell what a person typed from what the
                      platform generated. Rendered as a text node — React escapes
                      it, the same guarantee the email gets from escapeHtml. The
                      reason is staff free text, shown as TRF wrote it. */}
                  <span className="block pl-3 text-body-sm opacity-90">
                    {copy('apply.status.documents_attribution')}{' '}
                    <span className="italic" lang="en">“{d.reason}”</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {(app.flagged_documents ?? []).length > 0 && englishNote}
          {/* THE SENTENCE THIS EXISTS FOR. #1897 scoped the resubmission to the
              named document; the family was still being told to go and update
              the highlighted sections, which reads as “start again”. Shown only
              when a document is actually asked for, so a field-only ask keeps
              exactly the panel it had. Count-neutral by ruling (2026-09-07):
              staff may flag more than one kind in one request. */}
          {(app.flagged_documents ?? []).length > 0 && (
            <p className="text-body text-status-warn-fg">
              {copy('apply.status.documents_reassurance')}
            </p>
          )}
          <Button
            onClick={onContinue}
            variant="brand"
          >
            {copy('apply.status.needs_info_cta')}
          </Button>
        </div>
      )}

      {/* The note that came with the decision — approved, declined or waitlisted.
          Keyed on the stage (#1271), so a `committee_approved` application shows
          NOTHING: its `decision_reason` is the approval written out, and #998
          keeps that back until the offer goes out. */}
      {DECIDED.includes(stage) && app.decision_reason && (
        <div className="border border-border rounded-xl p-5 space-y-2">
          <MonoLabel className="block font-normal">
            {copy('apply.status.decision_note_label')}
          </MonoLabel>
          <p className="text-body leading-relaxed text-foreground" lang="en">
            {app.decision_reason}
          </p>
          {englishNote}
        </div>
      )}

      {/* #2236: a family can withdraw before committee approval (Mario,
          2026-10-13: from approval on, the family tells TRF and staff record
          it). Quiet on purpose — an outline button below everything else,
          never the screen's main action — and it always asks first, saying a
          mistake can be undone. `can_withdraw: false` marks a decline held
          back until its notice (#2233): it reads under_review, but the server
          would refuse the withdrawal. */}
      {onWithdraw && canFamilyWithdraw(app.status) && app.can_withdraw !== false && (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            {t('withdraw.action')}
          </Button>
          <AlertDialog open={confirmOpen} onOpenChange={(next) => { if (!withdrawing) setConfirmOpen(next); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('withdraw.confirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('withdraw.confirmBody')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={withdrawing}>{t('common:actions.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={withdrawing}
                  onClick={(e) => {
                    // Keep the dialog up until the write lands; the parent
                    // re-renders with the withdrawn record on success.
                    e.preventDefault();
                    setWithdrawing(true);
                    onWithdraw()
                      .then(() => setConfirmOpen(false))
                      .catch(() => setConfirmOpen(false))
                      .finally(() => setWithdrawing(false));
                  }}
                >
                  {t('withdraw.confirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ApplicationStatusPage() {
  const navigate = useNavigate();
  const [app, setApp] = useState<FamilyApplication | null>(null);
  const [loading, setLoading] = useState(true);
  // L5-b (D7): only a 404 ("No application yet") sends the family to the
  // wizard. Any other failure (5xx, offline) stays here with a retry: the
  // wizard re-reads, toasts and renders nothing, so a redirect there turned a
  // passing outage into a blank page.
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // #2977: undefined until known; see `SignatureStart`.
  const [signatureStart, setSignatureStart] = useState<SignatureStart | null | undefined>(undefined);
  const { copy, ready: copyReady } = useCopy();
  const { t } = useTranslation('common');
  const { t: tApp } = useTranslation('applications');
  const { toast } = useToast();
  const { labeler: fieldLabel, pending: fieldLabelsPending } = useFlaggedFieldLabelState(app);

  // #2236: the family's own withdrawal. On success the screen reads the
  // returned record; on failure it stays as it was and says so.
  // #2720: a refusal from a stale tab re-reads the application first. If the
  // withdrawal already happened (another tab), the screen shows the closed
  // state and never tells the family it failed.
  const withdraw = async () => {
    if (!app) return;
    try {
      const res = await api.applications.withdraw(app._id);
      if (res?.data) setApp(res.data);
    } catch (err) {
      if (isRefusedTransition(err)) {
        const current = await api.applications.getMine().then((r) => r?.data ?? null, () => null);
        if (current) setApp(current);
        if (current?.status === 'withdrawn') return;
      }
      // Mario, 2026-10-13: past committee approval only staff can record it
      // (a stale tab can still show the button). "Try again" would be false.
      if (serverCode(err) === WITHDRAW_NEEDS_STAFF) {
        toast({ title: tApp('withdraw.failedTitle'), description: tApp('withdraw.needsStaff') });
        throw err;
      }
      toast({ variant: 'destructive', title: tApp('withdraw.failedTitle'), description: tApp('withdraw.failed') });
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.applications
      .getMine()
      .then((res) => {
        if (cancelled) return;
        if (!res.data || res.data.status === 'draft') {
          navigate('/apply/wizard', { replace: true });
          return;
        }
        setApp(res.data);
        // #2977: a newly admitted family's grant, read before first paint so
        // the payment line never appears mid-read. A failed read leaves the
        // line out rather than say anything untrue.
        if (res.data.status === 'admitted' && !renewalMarkOf(res.data)) {
          return Promise.resolve()
            .then(() => api.grants.mine())
            .then(
              (grants) => { if (!cancelled) setSignatureStart(signatureStartOf(grants?.data)); },
              () => { if (!cancelled) setSignatureStart(undefined); },
            );
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 404) {
          navigate('/apply/wizard', { replace: true });
          return;
        }
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const retryLoad = () => {
    setLoadFailed(false);
    setLoading(true);
    setAttempt((n) => n + 1);
  };

  // Guardrail 7: the copy read folds into the wait this page ALREADY had, so
  // first paint is already resolved and a family never sees one wording
  // replaced by another mid-read. Both reads are in flight together, so this
  // costs no extra round trip in the common case.
  if (loading || !copyReady) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <MonoLabel className="block font-normal animate-pulse">
          {t('states.loading')}
        </MonoLabel>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 min-h-[60vh] px-4 text-center"
        role="alert"
        data-testid="application-status-load-failed"
      >
        <p className="text-body font-medium text-foreground">{t('states.error')}</p>
        <p className="text-body-sm text-stevens-ink-3">{t('states.pleaseTryAgain')}</p>
        <Button variant="outline" size="sm" onClick={retryLoad}>
          {t('actions.retry')}
        </Button>
      </div>
    );
  }

  if (!app) return null;

  return (
    <ApplicationStatusView
      app={app}
      copy={copy}
      fieldLabel={fieldLabel}
      fieldLabelsPending={fieldLabelsPending}
      onContinue={() => navigate('/apply/wizard')}
      onWithdraw={withdraw}
      onOpenGrants={() => navigate('/bills')}
      signatureStart={signatureStart}
    />
  );
}
